/**
 * @fileoverview Hook de Actions Layer para gestión de operaciones de negocio QR Message en WhatsApp.
 * Implementa el patrón de **Actions Layer** con separación estricta de responsabilidades, enfocado únicamente en operaciones de envío de mensajes y recordatorios QR.
 * Incluye optimizaciones de performance, manejo robusto de errores y documentación completa de flujos y tipos de datos.
 * @module useQrMessageActions
 * @category Hook - Actions Layer
 * @since 3.0.0
 * @version 3.1.0 - Optimización de performance, documentación JSDoc completa, separación de capas y manejo avanzado de errores
 */

/**
 * @typedef {Object} QrMessageData
 * @description Estructura de datos para el envío de mensajes QR.
 * @property {string} type - Tipo de mensaje ('text', 'image', 'document', 'video').
 * @property {boolean} template - Indica si el mensaje es plantilla.
 * @property {string} platform - Plataforma destino ('whatsapp').
 * @property {string|number} customer_id - ID del cliente destino.
 * @property {string} message_identifier - UUID único para el mensaje.
 * @property {File} [file_data] - Archivo adjunto si aplica.
 * @property {string} [caption] - Texto de pie de archivo si aplica.
 * @property {string} [message] - Mensaje de texto si aplica.
 */

/**
 * @typedef {Object} QrReminderData
 * @description Estructura de datos para la creación de recordatorios QR.
 * @property {string} template_text - Texto de la plantilla QR.
 * @property {string|number} customer_id - ID del cliente destino.
 * @property {Date} send_at - Fecha y hora de envío programado.
 * @property {string} timezone - Zona horaria del usuario.
 * @property {File} [file] - Archivo adjunto si aplica.
 */

/**
 * @typedef {Object} QrMessageActionsCallbacks
 * @description Callbacks para operaciones de envío y recordatorio QR.
 * @property {function} [onSuccess] - Callback en caso de éxito.
 * @property {function} [onError] - Callback en caso de error.
 */

import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import useCompleteMessageActions from 'presentation/hooks/inbox/actions/useCompleteMessageActions';
import { createReminder } from 'actions/whatsapp_karix';
import { customerPhoneNumber } from 'services/inbox/resolveNewChatMsIntegration';
import { getCountryFlagEmoji } from 'services/countryFlag';

/**
 * Hook de Actions Layer para gestión de operaciones de negocio QR Message.
 *
 * Implementa el patrón de **Actions Layer** con separación estricta de responsabilidades:
 * - ✅ Solo operaciones de negocio y mutaciones de estado relacionadas con mensajes y recordatorios QR.
 * - ✅ Performance optimizada con useCallback, useMemo y manejo eficiente de dependencias.
 * - ✅ Manejo robusto de errores y callbacks de éxito/fallo.
 * - ✅ No mezcla lógica de UI ni de formularios.
 *
 * **Flujo de operación:**
 * 1. Determina tipo de mensaje según archivo adjunto.
 * 2. Envía mensaje QR usando el método adecuado.
 * 3. Crea recordatorio QR programado.
 * 4. Orquesta la operación según tipo ('reminder' o mensaje directo).
 *
 * @function useQrMessageActions
 * @param {Object} params - Parámetros de inicialización del hook.
 * @param {Object} params.currentCustomer - Objeto del cliente actual (con id y datos relevantes).
 * @returns {Object} Operaciones de negocio: { sendMessage, sendReminderMessage, processQrMessage }
 * @see useCompleteMessageActions Para métodos de envío de mensajes
 * @see createReminder Acción Redux para crear recordatorios
 * @example
 * const { sendMessage, sendReminderMessage, processQrMessage } = useQrMessageActions({ ... });
 * @since 3.0.0
 * @version 3.1.0 - Optimización y documentación avanzada
 */
export default function useQrMessageActions({ currentCustomer }) {
  const dispatch = useDispatch();

  // Token CSRF para operaciones que lo requieren
  const csrfToken = document.querySelector('[name=csrf-token]')?.content;

  // Obtener acciones de mensajería desde hook existente
  const {
    sendMessageWithFilesOnly,
    sendMessageOnly,
  } = useCompleteMessageActions();

  /**
   * Determina el tipo de mensaje según el archivo adjunto.
   *
   * @function getMessageTypeFromFile
   * @memberof useQrMessageActions
   * @param {Array<File>} attachedFile - Array de archivos adjuntos.
   * @returns {string} Tipo de mensaje ('text', 'image', 'document', 'video').
   * @example
   * const type = getMessageTypeFromFile([file]);
   */
  const getMessageTypeFromFile = useCallback((attachedFile) => {
    if (!attachedFile || !Array.isArray(attachedFile) || attachedFile.length === 0) {
      return 'text';
    }

    const file = attachedFile[0];
    if (!file || !file.type) {
      return 'text';
    }

    const fileType = file.type;

    if (fileType.startsWith('image/')) {
      return 'image';
    } else if (fileType === 'application/pdf') {
      return 'document';
    } else if (fileType.startsWith('video/')) {
      return 'video';
    }

    return 'text';
  }, []);

  /**
   * Envía un mensaje QR usando el método adecuado según tipo de archivo.
   *
   * @async
   * @function sendMessage
   * @memberof useQrMessageActions
   * @param {Object} data - Datos del mensaje (texto, archivo adjunto).
   * @param {QrMessageActionsCallbacks} [callbacks] - Callbacks de éxito y error.
   * @returns {Promise<void>} Promesa que resuelve tras el envío.
   * @throws {Error} Si ocurre un error en el envío.
   * @example
   * await sendMessage({ templateText, attachedFile }, { onSuccess, onError });
   */
  const sendMessage = useCallback(async (data, callbacks = {}, msContext = {}) => {
    try {
      const messageType = getMessageTypeFromFile(data.attachedFile);

      /** @type {QrMessageData} */
      const messageData = {
        type: messageType,
        template: false,
        platform: msContext.isMsWhatsapp ? msContext.integration.platform : 'whatsapp',
        customer_id: currentCustomer.id,
        message_identifier: uuidv4(),
      };

      if (msContext.isMsWhatsapp && msContext.chatId) {
        messageData.chat_id = String(msContext.chatId);
      } else if (msContext.isMsWhatsapp && msContext.integration?.id) {
        messageData.platform_retailer_id = msContext.integration.id;
        messageData.create_chat = true;
        messageData.first_name = currentCustomer?.first_name;
        messageData.last_name = currentCustomer?.last_name;
        messageData.username = currentCustomer?.username;
        const phone = customerPhoneNumber(currentCustomer);
        if (phone) {
          messageData.phone_number = phone;
          const emojiFlag = getCountryFlagEmoji(currentCustomer?.country_id);
          if (emojiFlag) messageData.emoji_flag = emojiFlag;
        }
      }

      // Configurar datos específicos según tipo de mensaje
      const hasMediaContent = ["document", "image", "video"].includes(messageType);

      if (hasMediaContent) {
        messageData.file_data = data?.attachedFile[0];
        messageData.caption = data?.templateText || '';
      } else {
        messageData.message = data?.templateText || '';
      }

      const sendMethod = hasMediaContent ? sendMessageWithFilesOnly : sendMessageOnly;
      await sendMethod(messageData);

      // Ejecutar callback de éxito
      callbacks.onSuccess?.();
    } catch (error) {
      // Ejecutar callback de error
      callbacks.onError?.(error);
      throw error; // Re-throw para manejo superior si es necesario
    }
  }, [
    getMessageTypeFromFile,
    currentCustomer,
    sendMessageWithFilesOnly,
    sendMessageOnly
  ]);

  /**
   * Crea y envía un recordatorio QR programado usando el método adecuado.
   *
   * @async
   * @function sendReminderMessage
   * @memberof useQrMessageActions
   * @param {Object} data - Datos del recordatorio (texto, archivo adjunto, fecha).
   * @param {QrMessageActionsCallbacks} [callbacks] - Callbacks de éxito y error.
   * @returns {Promise<void>} Promesa que resuelve tras el envío.
   * @throws {Error} Si ocurre un error en la creación del recordatorio.
   * @example
   * await sendReminderMessage({ templateText, attachedFile, sendDate }, { onSuccess, onError });
   */
  const sendReminderMessage = useCallback(async (data, callbacks = {}, msContext = {}) => {
    try {
      const messageType = getMessageTypeFromFile(data.attachedFile);
      const timezone = new Date().toString().match(/([-+][0-9]+)\s/)?.[1] || '+0000';
      const params = new FormData();

      params.append('reminder[template_text]', data.templateText || '');
      params.append(
        'reminder[customer_id]',
        currentCustomer.customerId ?? currentCustomer.customer_id ?? currentCustomer.id
      );
      params.append('reminder[send_at]', moment(data.sendDate).format('YYYY-MM-DD HH:mm:ss'));
      params.append('reminder[timezone]', timezone);

      // Agregar archivo si es requerido
      if (["document", "image", "video"].includes(messageType) && data.attachedFile?.[0]) {
        params.append('reminder[file]', data.attachedFile[0]);
      }

      if (msContext.isMsWhatsapp && msContext.chatId) {
        params.append('reminder[chat_id]', String(msContext.chatId));
      } else if (msContext.isMsWhatsapp && msContext.integration?.id) {
        params.append('reminder[platform_retailer_id]', msContext.integration.id);
      }

      await dispatch(createReminder(params, csrfToken));

      // Ejecutar callback de éxito
      callbacks.onSuccess?.();
    } catch (error) {
      // Ejecutar callback de error
      callbacks.onError?.(error);
      throw error; // Re-throw para manejo superior si es necesario
    }
  }, [getMessageTypeFromFile, currentCustomer, dispatch, csrfToken]);

  /**
   * Orquesta la operación QR según tipo ('reminder' o mensaje directo).
   *
   * @async
   * @function processQrMessage
   * @memberof useQrMessageActions
   * @param {string} type - Tipo de operación ('reminder' o mensaje directo).
   * @param {Object} data - Datos del mensaje o recordatorio.
   * @param {QrMessageActionsCallbacks} [callbacks] - Callbacks de éxito y error.
   * @returns {Promise<void>} Promesa que resuelve tras la operación.
   * @example
   * await processQrMessage('reminder', { ... }, { onSuccess, onError });
   */
  const processQrMessage = useCallback(async (type, data, callbacks = {}, msContext = {}) => {
    if (type === 'reminder') {
      await sendReminderMessage(data, callbacks, msContext);
    } else {
      await sendMessage(data, callbacks, msContext);
    }
  }, [sendMessage, sendReminderMessage]);

  /**
   * Retorna las funciones de Actions Layer optimizadas para operaciones QR.
   *
   * @returns {Object} { sendMessage, sendReminderMessage, processQrMessage }
   */
  const returnValue = useMemo(() => ({
    sendMessage,
    sendReminderMessage,
    processQrMessage
  }), [
    sendMessage,
    sendReminderMessage,
    processQrMessage
  ]);

  return returnValue;
}